import { useEffect, useState, type FormEvent } from 'react';
import ManagedSignIn, { RecoveryCodesView } from '../../components/ManagedSignIn';
import { PASSWORD_MAX_CHARS, PASSWORD_MIN_CHARS, PASSWORD_RULES, passwordLength } from '../../services/activation';
import {
  changePassword,
  describeAMR,
  getAccountSecurity,
  passwordChangeNeedsCode,
  regenerateRecoveryCodes,
  revokeOtherSessions,
  revokeSession,
  type AccountSecurity as Data,
  type OwnSession,
} from '../../services/accountSecurity';
import { ApiError } from '../../services/tenancy';
import { beginLink, getMyMethods, setUpAuthenticator, unlinkMethod, type MyMethods, type SignInMethod } from '../../services/identityPlatform';
import type { PendingStep } from '../../services/managedSignIn';
import { useIdentity } from '../../services/identity';
import { METHOD_LABELS } from '../admin/peopleModel';

// My account: the person's sign-in methods (every session: link Microsoft/Google, remove a method -
// never the last one), and for a RadioOS password session also the password, authenticator /
// recovery codes and own sessions.

const field = 'w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm';
const btn = 'px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40';

const message = (err: unknown) => (err instanceof ApiError ? err.message : 'The request could not be completed. Please try again.');
const when = (iso: string) => new Date(iso).toLocaleString();

export function SessionsTable({ sessions, busy, onRevoke }: { sessions: OwnSession[]; busy?: boolean; onRevoke: (s: OwnSession) => void }) {
  return (
    <table className="w-full text-sm text-left">
      <caption className="sr-only">Your active sessions</caption>
      <thead className="text-gray-400">
        <tr><th scope="col" className="py-1">Session</th><th scope="col" className="py-1">Signed in with</th><th scope="col" className="py-1">Started</th>
          <th scope="col" className="py-1">Last used</th><th scope="col" className="py-1">Ends at the latest</th><th scope="col" className="py-1"><span className="sr-only">Action</span></th></tr>
      </thead>
      <tbody>
        {sessions.map((s) => (
          <tr key={s.id} className="border-t border-gray-700">
            <td className="py-1.5">{s.current ? <strong>This browser</strong> : (s.user_agent || 'Another browser')}</td>
            <td className="py-1.5">{describeAMR(s.amr)}</td>
            <td className="py-1.5">{when(s.created_at)}</td>
            <td className="py-1.5">{when(s.last_seen_at)}</td>
            <td className="py-1.5">{when(s.expires_at)}</td>
            <td className="py-1.5 text-right">
              <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy} onClick={() => onRevoke(s)}>{s.current ? 'Sign out' : 'End session'}</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PasswordChangeForm({ needCode, busy, error, done, onSubmit }: {
  needCode: boolean; busy?: boolean; error?: string; done?: string; onSubmit: (current: string, next: string, code: string) => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const length = passwordLength(next);
  const mismatch = confirm !== '' && confirm !== next;
  const ready = current && length >= PASSWORD_MIN_CHARS && length <= PASSWORD_MAX_CHARS && confirm === next && (!needCode || code.trim());
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(current, next, code.trim()); setCurrent(''); setNext(''); setConfirm(''); setCode(''); };
  return (
    <form className="space-y-3" onSubmit={submit}>
      <ul aria-label="Password requirements" className="text-xs text-gray-300 list-disc pl-5 space-y-1">{PASSWORD_RULES.map((r) => <li key={r}>{r}</li>)}</ul>
      <div><label htmlFor="pw-current" className="block text-xs text-gray-300 mb-1">Current password</label>
        <input id="pw-current" type="password" autoComplete="current-password" className={field} value={current} onChange={(e) => setCurrent(e.target.value)} /></div>
      <div><label htmlFor="pw-new" className="block text-xs text-gray-300 mb-1">New password</label>
        <input id="pw-new" type="password" autoComplete="new-password" className={field} value={next} onChange={(e) => setNext(e.target.value)} />
        <p className="text-xs text-gray-400 mt-1">{length} of at least {PASSWORD_MIN_CHARS} characters</p></div>
      <div><label htmlFor="pw-confirm" className="block text-xs text-gray-300 mb-1">Confirm new password</label>
        <input id="pw-confirm" type="password" autoComplete="new-password" className={field} value={confirm} onChange={(e) => setConfirm(e.target.value)}
          aria-invalid={mismatch ? true : undefined} />
        {mismatch && <p className="text-xs text-red-300 mt-1">The passwords do not match.</p>}</div>
      {needCode && (
        <div><label htmlFor="pw-code" className="block text-xs text-gray-300 mb-1">Code from your authenticator app</label>
          <input id="pw-code" autoComplete="one-time-code" inputMode="numeric" maxLength={7} className={`${field} font-mono`} value={code} onChange={(e) => setCode(e.target.value)} />
          <p className="text-xs text-gray-400 mt-1">You signed in with a recovery code, so a current authenticator code is also required.</p></div>
      )}
      {error && <p role="alert" className="text-sm text-red-200">{error}</p>}
      {done && <p role="status" className="text-sm text-green-200">{done}</p>}
      <button type="submit" className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={!ready || busy}>Change password</button>
    </form>
  );
}

export function RegenerateForm({ busy, error, onSubmit }: { busy?: boolean; error?: string; onSubmit: (password: string, code: string) => void }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  return (
    <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); onSubmit(password, code.trim()); setPassword(''); setCode(''); }}>
      <p className="text-sm text-gray-300">New codes replace all earlier recovery codes. Your password and a current authenticator code are required.</p>
      <div><label htmlFor="rc-password" className="block text-xs text-gray-300 mb-1">Password</label>
        <input id="rc-password" type="password" autoComplete="current-password" className={field} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
      <div><label htmlFor="rc-code" className="block text-xs text-gray-300 mb-1">Code from your authenticator app</label>
        <input id="rc-code" autoComplete="one-time-code" inputMode="numeric" maxLength={7} className={`${field} font-mono`} value={code} onChange={(e) => setCode(e.target.value)} /></div>
      {error && <p role="alert" className="text-sm text-red-200">{error}</p>}
      <button type="submit" className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={busy || !password || !code.trim()}>Generate new recovery codes</button>
    </form>
  );
}

export function AccountSecurityView({ data, children }: { data: Data; children?: React.ReactNode }) {
  const a = data.account;
  return (
    <div className="space-y-2">
      <h2 className="text-xl font-semibold">Account security</h2>
      <dl className="text-sm grid grid-cols-3 gap-2">
        <dt className="text-gray-400">Sign-in e-mail</dt><dd className="col-span-2">{a.login_email}</dd>
        <dt className="text-gray-400">Password set</dt><dd className="col-span-2">{a.password_set_at ? when(a.password_set_at) : '—'}</dd>
        <dt className="text-gray-400">Authenticator app</dt><dd className="col-span-2">{a.mfa.enrolled ? 'Set up' : a.mfa.required ? 'Required, not set up' : 'Not used'}</dd>
        {a.mfa.enrolled && <><dt className="text-gray-400">Recovery codes left</dt><dd className="col-span-2">{a.mfa.recovery_codes_remaining} of 10</dd></>}
        <dt className="text-gray-400">This session</dt><dd className="col-span-2">{describeAMR(data.current_amr)}</dd>
      </dl>
      {children}
    </div>
  );
}

const LINK_ERRORS: Record<string, string> = {
  link: 'That account could not be linked: it is already linked to a RadioOS user, or you already have an account of this provider.',
  expired: 'The linking expired or your session ended. Sign in again and retry.',
  disabled: 'This sign-in method is switched off.',
};

/** The person's sign-in methods (pure view). */
export function SignInMethodsView({ data, busy, notice, onLink, onUnlink }: {
  data: MyMethods; busy?: boolean; notice?: string; onLink: (p: 'microsoft' | 'google') => void; onUnlink: (m: SignInMethod) => void;
}) {
  const only = data.methods.length <= 1;
  const linked = new Set(data.methods.map((m) => m.type));
  return (
    <section className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
      <h3 className="font-semibold">Sign-in methods</h3>
      <p className="text-sm text-gray-400">Every method signs you in as the same person. Adding or removing one needs your current RadioOS sign-in (with multi-factor authentication when your role requires it).</p>
      {notice && <p role="status" className="text-sm text-yellow-200">{notice}</p>}
      <ul className="divide-y divide-gray-700">
        {data.methods.map((m) => (
          <li key={m.id || m.type} className="py-2 flex items-center gap-3">
            <div className="flex-1">
              <div>{METHOD_LABELS[m.type] || m.type}{m.account ? ` - ${m.account}` : ''}</div>
              <div className="text-xs text-gray-400">
                {m.type === 'radioos' ? (m.password_set ? (m.mfa_enrolled ? 'Password and authenticator app set up' : 'Password set; no authenticator app yet') : 'Password not set yet') : `Linked ${when(m.linked_at)}`}
              </div>
            </div>
            {m.id && (
              <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy || only} title={only ? 'Your only sign-in method cannot be removed' : undefined}
                onClick={() => onUnlink(m)}>Remove</button>
            )}
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        {data.linkable.filter((p) => !linked.has(p)).map((p) => (
          <button key={p} className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={busy} onClick={() => onLink(p)}>Link {METHOD_LABELS[p]}</button>
        ))}
      </div>
    </section>
  );
}

/**
 * The RadioOS authenticator app for a Microsoft/Google session (pure view): whether it is set up,
 * and - when it is not - the button that opens its enrolment. With it, a sign-in without MFA
 * evidence is confirmed by the app instead of depending on a provider security override.
 */
export function AuthenticatorView({ data, busy, onSetUp }: { data: MyMethods; busy?: boolean; onSetUp: () => void }) {
  if (!data.authenticator_setup_available && !data.authenticator) return null;
  return (
    <section className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
      <h3 className="font-semibold">Authenticator app</h3>
      {data.authenticator ? (
        <p className="text-sm text-gray-300">Set up. When a Microsoft or Google sign-in does not prove multi-factor authentication, RadioOS asks for its code.</p>
      ) : (
        <>
          <p className="text-sm text-gray-300">
            Not set up. Set up an authenticator app (with recovery codes) so that a Microsoft or Google sign-in that does not prove
            multi-factor authentication can be confirmed with it.
          </p>
          <button className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={busy} onClick={onSetUp}>Set up authenticator app</button>
        </>
      )}
    </section>
  );
}

function AuthenticatorSection() {
  const [data, setData] = useState<MyMethods | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [pending, setPending] = useState<PendingStep | null>(null);
  useEffect(() => { getMyMethods().then(setData, () => undefined); }, []);
  if (pending) {
    return (
      <section className="bg-gray-800 border border-gray-700 rounded p-4 max-w-md">
        <ManagedSignIn pending={pending} onSignedIn={() => window.location.reload()} />
      </section>
    );
  }
  if (!data) return null;
  return (
    <>
      {notice && <p role="alert" className="text-sm text-red-200">{notice}</p>}
      <AuthenticatorView data={data} busy={busy} onSetUp={async () => {
        setBusy(true);
        try {
          const r = await setUpAuthenticator();
          setPending({ challenge: r.challenge, next: r.next });
        } catch (e) { setNotice(message(e)); } finally { setBusy(false); }
      }} />
    </>
  );
}

function SignInMethodsSection() {
  const [data, setData] = useState<MyMethods | null>(null);
  const [busy, setBusy] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const initial = params.get('linked') ? `${METHOD_LABELS[params.get('linked') as 'google'] || 'The account'} is now linked.`
    : params.get('link_error') ? LINK_ERRORS[params.get('link_error') || ''] || 'The account could not be linked.' : undefined;
  const [notice, setNotice] = useState<string | undefined>(initial);
  const load = () => getMyMethods().then(setData, (e) => setNotice(message(e)));
  useEffect(() => { load(); }, []);
  if (!data) return notice ? <p role="alert" className="text-sm text-red-200">{notice}</p> : null;
  return (
    <SignInMethodsView data={data} busy={busy} notice={notice}
      onLink={async (p) => {
        setBusy(true);
        try { window.location.assign((await beginLink(p)).redirect_url); } catch (e) { setNotice(message(e)); setBusy(false); }
      }}
      onUnlink={async (m) => {
        if (!window.confirm(`Remove ${METHOD_LABELS[m.type]}${m.account ? ` (${m.account})` : ''} as a way to sign in?`)) return;
        setBusy(true);
        try { await unlinkMethod(m.id || ''); setNotice('The sign-in method was removed.'); await load(); } catch (e) { setNotice(message(e)); } finally { setBusy(false); }
      }} />
  );
}

export default function AccountSecurityPage() {
  const { me } = useIdentity();
  if (me && me.auth_method !== 'password') {
    // Microsoft / Google session: the methods only (the password part is the RadioOS sign-in's).
    return (
      <div className="h-full overflow-y-auto max-w-4xl mx-auto px-6 py-8 space-y-6">
        <h2 className="text-xl font-semibold">My account</h2>
        <dl className="text-sm grid grid-cols-3 gap-2">
          <dt className="text-gray-400">Person</dt><dd className="col-span-2">{me.display_name || me.username}{me.email ? ` (${me.email})` : ''}</dd>
          <dt className="text-gray-400">This session</dt><dd className="col-span-2">{describeAMR(me.amr)}</dd>
        </dl>
        <SignInMethodsSection />
        <AuthenticatorSection />
      </div>
    );
  }
  return <ManagedAccountPage />;
}

function ManagedAccountPage() {
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [pwError, setPwError] = useState<string>();
  const [pwDone, setPwDone] = useState<string>();
  const [rcError, setRcError] = useState<string>();
  // New recovery codes: memory only, shown once.
  const [codes, setCodes] = useState<string[] | null>(null);

  const load = () => getAccountSecurity().then(setData, (e) => setError(message(e)));
  useEffect(() => { load(); }, []);
  const run = async (fn: () => Promise<void>) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  if (error) return <p role="alert" className="p-6 text-sm text-red-200">{error}</p>;
  if (!data) return <p role="status" className="p-6 text-sm text-gray-400">Loading…</p>;
  if (codes) {
    return (
      <div className="max-w-md mx-auto p-6">
        <RecoveryCodesView codes={codes} account={data.account.login_email} onContinue={() => { setCodes(null); load(); }} />
      </div>
    );
  }
  return (
    <div className="h-full overflow-y-auto max-w-4xl mx-auto px-6 py-8 space-y-6">
      <AccountSecurityView data={data} />
      <SignInMethodsSection />
      <section className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
        <h3 className="font-semibold">Change password</h3>
        <PasswordChangeForm needCode={passwordChangeNeedsCode(data.current_amr, data.account.mfa.enrolled)} busy={busy} error={pwError} done={pwDone}
          onSubmit={(c, n, code) => run(async () => {
            setPwError(undefined); setPwDone(undefined);
            try {
              const r = await changePassword(c, n, code);
              setPwDone(`Password changed. ${r.other_sessions_revoked} other session(s) were signed out.`);
              await load();
            } catch (e) { setPwError(message(e)); }
          })} />
      </section>
      {data.account.mfa.enrolled && (
        <section className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
          <h3 className="font-semibold">Recovery codes</h3>
          <RegenerateForm busy={busy} error={rcError} onSubmit={(p, c) => run(async () => {
            setRcError(undefined);
            try { setCodes((await regenerateRecoveryCodes(p, c)).recovery_codes); } catch (e) { setRcError(message(e)); }
          })} />
        </section>
      )}
      <section className="bg-gray-800 border border-gray-700 rounded p-4 space-y-3">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold">Active sessions</h3>
          <button className={`${btn} ml-auto bg-gray-700 hover:bg-gray-600`} disabled={busy || data.sessions.length < 2}
            onClick={() => run(async () => { await revokeOtherSessions(); await load(); })}>Sign out all other sessions</button>
        </div>
        <SessionsTable sessions={data.sessions} busy={busy} onRevoke={(s) => run(async () => {
          const r = await revokeSession(s.id);
          if (r.current) window.location.assign('/');
          else await load();
        })} />
      </section>
    </div>
  );
}
